import { IServerSideGetRowsRequest } from 'ag-grid-community';
import get from 'lodash/get';
import memoize from 'lodash/memoize';

import { ViewColumn } from 'common/types/viewColumn';

import { MetadataProvider } from '../dataProviders';
import { getSoqlDataProviderConfigFromVif } from '../dataProviders/DataProviderHelper';
import InlineDataProvider from '../dataProviders/InlineDataProvider';
import SocrataViewDataProvider from '../dataProviders/SocrataViewDataProvider';
import SoqlDataProvider from '../dataProviders/SoqlDataProvider';
import { SoqlDataProviderConfig } from '../dataProviders/types';
import { DataProviderConfig } from '../types/tableData';
import { Vif } from '../vif';

import { countAlias, whereClauseFilteringOwnColumn } from './SoqlHelpers';
import { isDoingGrouping, tableNullGroupingCountQuery, tableRowCountQuery } from './TableDataQueryHelpers';

/**
 * Utilizes the InlineDataProvider, ie: summary tables, SOQL query editor.
 * @param {Object} vifForRowCount - Vif of the given table.
 * @returns {Promise} Row count.
 */
export function getInlineDataRowCount(vifWithInlineData: Vif): Promise<any> {
  const inlineDataProvider = new InlineDataProvider(vifWithInlineData);
  return Promise.resolve(inlineDataProvider.getTotalRowCount());
}

/**
 * @param {Object} vifForRowCount - Vif of the given table.
 * @param {Object} dataProviderConfig - An object of values required by the data providers.
 * @param {string} dataProviderConfig.datasetUid - Uid of the given table.
 * @param {string} dataProviderConfig.domain - The domain on which the table lives. If federated, this will be the source
 * @param {Boolean} dataProviderConfig.readFromNbe: get(vifForRowCount, 'series[0].dataSource.readFromNbe', true)
 * @param {Object} datasetMetadata - metadata of source dataset
 * @returns {Promise} Row count.
 */
export function getSoqlDataRowCount(
  vifForRowCount: Vif,
  dataProviderConfig: DataProviderConfig,
  datasetMetadata: any,
): Promise<any> {
  const soqlDataProvider = new SoqlDataProvider(dataProviderConfig, true);
  const whereClauseComponents = whereClauseFilteringOwnColumn(vifForRowCount, 0);

  // This is stored as a variable and not a function since we need to capture
  // the output of _.memoize.
  const getMemoizedRowCount = memoize(
    function (_soqlDataProvider, _whereClauseComponents, lastUpdate) {
      return _soqlDataProvider.getRowCount(_whereClauseComponents);
    },
    function (_soqlDataProvider, _whereClauseComponents, lastUpdate) {
      const datasetUid = _soqlDataProvider.getConfigurationProperty('datasetUid');

      return `${datasetUid}_${_whereClauseComponents}_${lastUpdate}`;
    },
  );
  return getMemoizedRowCount(soqlDataProvider, whereClauseComponents, datasetMetadata.rowsUpdatedAt);
}

/**
 * @param {Object} vifForRowCount - Vif of the given table.
 * @param {Object} dataProviderConfig - An object of values required by the data providers.
 * @param {string} dataProviderConfig.datasetUid - Uid of the given table.
 * @param {string} dataProviderConfig.domain - The domain on which the table lives. If federated, this will be the source
 * @param {Boolean} dataProviderConfig.readFromNbe: get(vifForRowCount, 'series[0].dataSource.readFromNbe', true)
 * @param {Object} datasetMetadata - metadata of source dataset
 * @param {string} resourceURI - Location of source dataset
 * @param {Object} displayableColumns - all possible displayable columns of underlying dataset, used for generating the row count query
 * @param {string} whereClause - Where clause generated from applying filters to dataset, used for the row count query
 * @param {Request} agRequest - The request being made by agGrid to get the rows, shows if grouping is being done or not
 * @param {string} searchString - An optional search string used by Primer
 * @returns {Promise} Row count.
 */
export function getAgSoqlDataRowCount(
  dataProviderConfig: SoqlDataProviderConfig,
  datasetMetadata: any,
  displayableColumns: ViewColumn[],
  whereClause: string,
  agRequest: IServerSideGetRowsRequest,
  searchString?: string,
): Promise<any> {
  const soqlDataProvider = new SoqlDataProvider(dataProviderConfig, true);

  // This is stored as a variable and not a function since we need to capture
  // the output of _.memoize.
  const getRowCount = memoize(
    async function (_soqlDataProvider, _whereClause, _agRequest, lastUpdate) {
      const rowCountResult: any = await _soqlDataProvider.invokeSoqlQuery(tableRowCountQuery, [
        displayableColumns,
        _whereClause,
        _agRequest,
        searchString,
      ]);
      const rowCount = parseInt(get(rowCountResult, [0, countAlias()], '0'), 10);
      if (isDoingGrouping(_agRequest.rowGroupCols, _agRequest.groupKeys)) {
        const nullGroupedRowCount: any = await _soqlDataProvider.invokeSoqlQuery(
          tableNullGroupingCountQuery,
          [displayableColumns, _whereClause, _agRequest],
        );
        const groupHasNulls = parseInt(get(nullGroupedRowCount, [0, countAlias()], '0'), 10) > 0;
        return rowCount + (groupHasNulls ? 1 : 0);
      }
      return rowCount;
    },
    function (_soqlDataProvider, _whereClause, _agRequest, lastUpdate) {
      const datasetUid = _soqlDataProvider.getConfigurationProperty('datasetUid');

      return `${datasetUid}_${_whereClause}_${_agRequest}_${lastUpdate}`;
    },
  );

  return getRowCount(soqlDataProvider, whereClause, agRequest, datasetMetadata.rowsUpdatedAt);
}

/**
 * Utilizes the SoqlDataProvider, but expects raw Soql.
 * @param {Object} vifForRowCount - Vif of the given table.
 * @param {Object} dataProviderConfig - An object of values required by the data providers.
 * @param {string} dataProviderConfig.datasetUid - Uid of the given table.
 * @param {string} dataProviderConfig.domain - The domain on which the table lives. If federated, this will be the source
 * @param {Boolean} dataProviderConfig.readFromNbe: get(vifForRowCount, 'series[0].dataSource.readFromNbe', true)
 * @returns {Promise} Row count.
 */
export function getRawSoqlRowCount(
  vifForRowCount: Vif,
  dataProviderConfig: SoqlDataProviderConfig,
): Promise<number> {
  const soqlQuery = get(vifForRowCount, 'series[0].dataSource.rawSoqlQuery');
  const soqlDataProvider = new SoqlDataProvider(dataProviderConfig, true);
  return soqlDataProvider.getRowCountForQuery(soqlQuery);
}

/**
 * Utilizes the SocrataViewDataProvider, ie: the /data view, Socrata Data Player embeds
 * @param {Object} vifForRowCount - Vif of the given table.
 * @param {Object} dataProviderConfig - An object of values required by the data providers.
 * @param {string} dataProviderConfig.datasetUid - Uid of the given table.
 * @param {string} dataProviderConfig.domain - The domain on which the table lives. If federated, this will be the source
 * @param {Boolean} dataProviderConfig.readFromNbe: get(vifForRowCount, 'series[0].dataSource.readFromNbe', true)
 * @returns {Promise} Row count.
 */
export function getSocrataViewRowCount(
  vifForRowCount: Vif,
  dataProviderConfig: SoqlDataProviderConfig,
): Promise<number> {
  const queryConfig = {
    currentView: get(vifForRowCount, 'series[0].dataSource.currentView', {}),
    savedView: get(vifForRowCount, 'series[0].dataSource.savedView', null),
    parentView: get(vifForRowCount, 'series[0].dataSource.parentView', null),
    offset: 0,
    limit: 0,
  };

  const socrataViewDataProvider = new SocrataViewDataProvider({
    domain: get(vifForRowCount, 'series[0].dataSource.domain'),
  });
  const soqlDataProvider = new SoqlDataProvider(dataProviderConfig, true);
  const query = socrataViewDataProvider.generateQuery(queryConfig);
  const viewId = socrataViewDataProvider.getViewId(queryConfig);
  return soqlDataProvider.getRowCountForQuery(query, viewId);
}

// Rather than have each data source fetch row counts as part of their
// set_____DataQuery() methods which fetch the actual rows, breaking
// row count fetches into separate methods here. Each get_____RowCount()
// method should return a promise which resolves to the row count
export async function getDatasetRowCount(
  vifForRowCount: Vif,
  displayableColumns: ViewColumn[],
  whereClause: string,
  agRequest: IServerSideGetRowsRequest,
  searchString?: string,
) {
  const dataSourceType = get(vifForRowCount, 'series[0].dataSource.type');
  const soqlDataProviderConfig = getSoqlDataProviderConfigFromVif(vifForRowCount);

  switch (dataSourceType) {
    case 'socrata.inline':
      return getInlineDataRowCount(vifForRowCount);
    case 'socrata.soql':
      return getAgSoqlDataRowCount(
        soqlDataProviderConfig,
        await MetadataProvider.fromVif(vifForRowCount).getDatasetMetadata(),
        displayableColumns,
        whereClause,
        agRequest,
        searchString,
      );
    case 'socrata.rawSoql':
      return getRawSoqlRowCount(vifForRowCount, soqlDataProviderConfig);
    case 'socrata.view':
      return getSocrataViewRowCount(vifForRowCount, soqlDataProviderConfig);
    default:
      return Promise.reject(`Invalid data source type in vif: '${dataSourceType}'.`);
  }
}
